/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useLayoutEffect, useMemo, useCallback, useState, useEffect, useRef } from "react";

import GridLayout, {Responsive, WidthProvider} from "react-grid-layout";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
// import ReactGridLayout from "../react/ReactGridLayout.tsx";
// import DivE from "../react/DivE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getLng, getDict, getTheme, getAppState, changeAppState, getUser, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {screenSizesType, LayoutItem, LayoutType, gridLayoutProps} from "../react/ReactGridLayout.tsx";
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

type Tcomponent = {
  compactType:"vertical" | "horizontal" | null,
  allowOverlap:boolean,
  preventCollision:boolean,
  responsive:boolean
};

const ResponsiveGridLayout = WidthProvider(Responsive);

// const { Option } = Select;
const { rz } = window;

const RZreactGridLayout:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZreactGridLayout.displayName = "ReactGridLayout";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const theme = useAppSelector(getTheme);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type, content} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key));
  const elState = appState[fullPath];
  const rootNamespace = useMemo(() => (window[rootAttributes.key || rz.strMakeID(24)]), [rootAttributes.key]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const cmpType = useMemo<Tcomponent>(() => {
    const typ:Tcomponent = {compactType: null, allowOverlap: false, preventCollision: true, responsive: false};
    (type || "").replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["vertical", "verticalCompact"].indexOf(tt)) { typ.compactType = "vertical" }
      else if (~["horizontal", "horizontalCompact"].indexOf(tt)) { typ.compactType = "horizontal" }
      else if (~["responsive"].indexOf(tt)) { typ.responsive = true }
      else if (~["allowOverlap", "allowoverlap", "overlap"].indexOf(tt)) { typ.allowOverlap = true }
      else if (~["preventCollision", "noCollision", "preventcollision"].indexOf(tt)) { typ.preventCollision = true }
    });

    if (attributes?.compactType) { typ.compactType = attributes.compactType }
    if (attributes?.allowOverlap) { typ.allowOverlap = (attributes.allowOverlap === "true") }
    if (attributes?.preventCollision) { typ.preventCollision = (attributes.preventCollision === "true") }
    if (attributes?.responsive) { typ.responsive = (attributes.responsive === "true") }
    return typ;
  }, [type, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if (!~userClss.indexOf("layout")) { userClss.push("layout") }
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.disabled, elState?.userClass, permissions.disabled]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (attributes.color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (attributes.backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    return est;
  }, [elState.userStyle, elState?.visible, permissions.visible, attributes.color, attributes.backgroundColor, theme]);

  // const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng, rlng]);

  const [layouts, setLayuot] = useState<LayoutType>();
  const nonResponsiveWrapperRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const cols = useMemo(() => {
    if (attributes.cols && rz.isString(attributes.cols) && attributes.cols.startsWith("{") && attributes.cols.endsWith("}")) {
      const jsn = rz.objFromJSON((rz.strReplaceAll(attributes.cols, "'", "\"")).trim());
      if (jsn && jsn.lg) {
        if (!jsn.md) { jsn.md = jsn.lg }
        if (!jsn.sm) { jsn.sm = jsn.md || jsn.lg }
        if (!jsn.xs) { jsn.xs = jsn.sm || jsn.md || jsn.lg }
        if (!jsn.xxs) { jsn.xxs = jsn.xs || jsn.sm || jsn.md || jsn.lg }
        return jsn;
      }
    }
    else if (rz.isString(attributes.cols) && (/^\d+$/).test(attributes.cols)) { return Number(attributes.cols) }
    return undefined;
  }, [attributes.cols]);

  const breakpoints = useMemo(() => {
    if (attributes.breakpoints && rz.isString(attributes.breakpoints) && attributes.breakpoints.startsWith("{") && attributes.breakpoints.endsWith("}")) {
      const jsn = rz.objFromJSON((rz.strReplaceAll(attributes.breakpoints, "'", "\"")).trim());
      if (jsn && jsn.lg) {
        if (!jsn.md) { jsn.md = jsn.lg }
        if (!jsn.sm) { jsn.sm = (jsn.md || jsn.lg) - 1 }
        if (!jsn.xs) { jsn.xs = (jsn.sm || jsn.md || jsn.lg) - 2 }
        if (!jsn.xxs) { jsn.xxs = (jsn.xs || jsn.sm || jsn.md || jsn.lg) - 3 }
        return jsn;
      }
    }
    return { lg: 1200, md: 992, sm: 768, xs: 576, xxs: 0 };
  }, [attributes.breakpoints]);

  const margin = useMemo(() => {
    if (elState.attributes.margin) {
      return [Number(elState.attributes.margin), Number(elState.attributes.margin)];
    }
    return [10, 10];
  }, [elState.attributes.margin]);

  const rowHeight = useMemo(() => {
    if (elState.attributes.rowHeight) {
      return Number(elState.attributes.rowHeight);
    }
    return 60;
  }, [elState.attributes.rowHeight]);

  const resizeHandles = useMemo(() => {
    if (elState.attributes.resizeHandles) {
      return elState.attributes.resizeHandles.replace((/(\s+|_|\|)/igm), " ").split(" ");
    }
    return ["se"];
  }, [elState.attributes.resizeHandles]);

  const onClick = useCallback(({event, itemKey, allItems}) => {
    if (fnc.config?.isDev) console.log({action: "<ReactGridLayout key='" + key + "'> -> onClick()", event, itemKey, allItems});
    const attr = attributes.onClick || attributes.onclick;
    if (rootNamespace && attr && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, event, itemKey, allItems, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, event, itemKey, allItems, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [attributes, dct, dispatch, key, lng, rootAttributes.key, rootNamespace, user, safe]);

  const childrenItems = useMemo<Array<React.ReactNode>>(() => {
    if (!content || !content.length) { return [] }
    const chldrns:Array<React.ReactNode> = [];
    const isResponsive = (cmpType.responsive || rz.isObject(cols));
    const louts = (isResponsive ? {lg: [], md: [], sm: [], xs: [], xxs: []} : []);

    let k = 0;
    content.forEach((item) => {
      const cState = appState[item.fullPath], cellstyle = {...cState.userStyle}, cellclass = rz.objClone(cState?.userClass || []);
      const cpermissions = fnc.checkPermissions(user, {...cState.attributes, rootAttributes: {key: rootAttributes.key}});
      if ((cState?.visible === false || cpermissions.visible === false) && cellstyle.display !== "none") { cellstyle.display = "none" }
      else if ((cState?.visible === true && cpermissions.visible !== false) && cellstyle.display === "none") { cellstyle.display = undefined }
      if ((cState?.disabled || cpermissions.disabled === true) && !~cellclass.indexOf("uk-disabled")) { cellclass.push("uk-disabled") }

      if (cellstyle.display !== "none") {
        if (isResponsive) {
          let lgObj = cState.attributes.lg || cState.attributes.md || {}; if (rz.isString(lgObj) && lgObj.startsWith("{") && lgObj.endsWith("}")) { lgObj = rz.objFromJSON((rz.strReplaceAll(lgObj, "'", "\"")).trim()) }
          let mdObj = cState.attributes.md || cState.attributes.lg || {}; if (rz.isString(mdObj) && mdObj.startsWith("{") && mdObj.endsWith("}")) { mdObj = rz.objFromJSON((rz.strReplaceAll(mdObj, "'", "\"")).trim()) }
          let smObj = cState.attributes.sm || cState.attributes.md || cState.attributes.lg || {}; if (rz.isString(smObj) && smObj.startsWith("{") && smObj.endsWith("}")) { smObj = rz.objFromJSON((rz.strReplaceAll(smObj, "'", "\"")).trim()) }
          // eslint-disable-next-line max-len
          let xsObj = cState.attributes.xs || cState.attributes.sm || cState.attributes.md || cState.attributes.lg || {}; if (rz.isString(xsObj) && xsObj.startsWith("{") && xsObj.endsWith("}")) { xsObj = rz.objFromJSON((rz.strReplaceAll(xsObj, "'", "\"")).trim()) }
          // eslint-disable-next-line max-len
          let xxsObj = cState.attributes.xxs || cState.attributes.xs || cState.attributes.sm || cState.attributes.md || cState.attributes.lg || {}; if (rz.isString(xxsObj) && xxsObj.startsWith("{") && xxsObj.endsWith("}")) { xxsObj = rz.objFromJSON((rz.strReplaceAll(xxsObj, "'", "\"")).trim()) }

          // eslint-disable-next-line max-len
          const lg = {i: item.key, w: lgObj?.w ?? 8, h: lgObj?.h ?? 8, x: lgObj?.x ?? k, y: lgObj?.y ?? k, minW: lgObj?.minW, maxW: lgObj?.maxW, minH: lgObj?.minH, maxH: lgObj?.maxH, isDraggable: !(lgObj?.isDraggable === "false" || lgObj?.isDraggable === false), isResizable: !(lgObj?.isResizable === "false" || lgObj?.isResizable === false), static: !(lgObj?.static === "false" || lgObj?.static === false)};
          // eslint-disable-next-line max-len
          const md = {i: item.key, w: mdObj?.w ?? 6, h: mdObj?.h ?? 6, x: mdObj?.x ?? k, y: mdObj?.y ?? k, minW: mdObj?.minW, maxW: mdObj?.maxW, minH: mdObj?.minH, maxH: mdObj?.maxH, isDraggable: !(mdObj?.isDraggable === "false" || mdObj?.isDraggable === false), isResizable: !(mdObj?.isResizable === "false" || mdObj?.isResizable === false), static: !(mdObj?.static === "false" || mdObj?.static === false)};
          // eslint-disable-next-line max-len
          const sm = {i: item.key, w: smObj?.w ?? 4, h: smObj?.h ?? 4, x: smObj?.x ?? k, y: smObj?.y ?? k, minW: smObj?.minW, maxW: smObj?.maxW, minH: smObj?.minH, maxH: smObj?.maxH, isDraggable: !(smObj?.isDraggable === "false" || smObj?.isDraggable === false), isResizable: !(smObj?.isResizable === "false" || smObj?.isResizable === false), static: !(smObj?.static === "false" || smObj?.static === false)};
          // eslint-disable-next-line max-len
          const xs = {i: item.key, w: xsObj?.w ?? 4, h: xsObj?.h ?? 4, x: xsObj?.x ?? k, y: xsObj?.y ?? k, minW: xsObj?.minW, maxW: xsObj?.maxW, minH: xsObj?.minH, maxH: xsObj?.maxH, isDraggable: !(xsObj?.isDraggable === "false" || xsObj?.isDraggable === false), isResizable: !(xsObj?.isResizable === "false" || xsObj?.isResizable === false), static: !(xsObj?.static === "false" || xsObj?.static === false)};
          // eslint-disable-next-line max-len
          const xxs = {i: item.key, w: xxsObj?.w ?? 2, h: xxsObj?.h ?? 2, x: xxsObj?.x ?? k, y: xxsObj?.y ?? k, minW: xxsObj?.minW, maxW: xxsObj?.maxW, minH: xxsObj?.minH, maxH: xxsObj?.maxH, isDraggable: !(xxsObj?.isDraggable === "false" || xxsObj?.isDraggable === false), isResizable: !(xxsObj?.isResizable === "false" || xxsObj?.isResizable === false), static: !(xxsObj?.static === "false" || xxsObj?.static === false)};

          if (cState.attributes.isDraggable || cState.attributes.draggable) {
            const draggable = ((cState.attributes.isDraggable || cState.attributes.draggable) === "true");
            lg.isDraggable = draggable; md.isDraggable = draggable; sm.isDraggable = draggable; xs.isDraggable = draggable; xxs.isDraggable = draggable;
          }
          if (cState.attributes.isResizable || cState.attributes.resizable) {
            const resizable = ((cState.attributes.isResizable || cState.attributes.resizable) === "true");
            lg.isResizable = resizable; md.isResizable = resizable; sm.isResizable = resizable; xs.isResizable = resizable; xxs.isResizable = resizable;
          }
          if (cState.attributes.static) {
            const static1 = (cState.attributes.static === "true");
            lg.static = static1; md.static = static1; sm.static = static1; xs.static = static1; xxs.static = static1;
          }
          if (cState.attributes.x) { const x = Number(cState.attributes.x); lg.x = x; md.x = x; sm.x = x; xs.x = x; xxs.x = x }
          if (cState.attributes.y) { const y = Number(cState.attributes.y); lg.y = y; md.y = y; sm.y = y; xs.y = y; xxs.y = y }
          if (cState.attributes.w) { const w = Number(cState.attributes.w); lg.w = w; md.w = w; sm.w = w; xs.w = w; xxs.w = w }
          if (cState.attributes.h) { const h = Number(cState.attributes.h); lg.h = h; md.h = h; sm.h = h; xs.h = h; xxs.h = h }
          if (cState.attributes.minW) { const minW = Number(cState.attributes.minW); lg.minW = minW; md.minW = minW; sm.minW = minW; xs.minW = minW; xxs.minW = minW }
          if (cState.attributes.maxW) { const maxW = Number(cState.attributes.maxW); lg.maxW = maxW; md.maxW = maxW; sm.maxW = maxW; xs.maxW = maxW; xxs.maxW = maxW }
          if (cState.attributes.minH) { const minH = Number(cState.attributes.minH); lg.minH = minH; md.minH = minH; sm.minH = minH; xs.minH = minH; xxs.minH = minH }
          if (cState.attributes.maxH) { const maxH = Number(cState.attributes.maxH); lg.maxH = maxH; md.maxH = maxH; sm.maxH = maxH; xs.maxH = maxH; xxs.maxH = maxH }

          // @ts-ignore
          louts.lg.push(lg); louts.md.push(md); louts.sm.push(sm); louts.xs.push(xs); louts.xxs.push(xxs);

          chldrns.push(
            <div key={item.key} id={item.key} className={cellclass.join(" ")} style={cellstyle} onClick={(event) => { onClick({event, itemKey: item.key, allItems: content}) }}>
              {(item.content || []).map((el) => (el.children))}
            </div>
          );
        }
        else {
          let lyt = cState.attributes.layout || {}; if (rz.isString(lyt) && lyt.startsWith("{") && lyt.endsWith("}")) { lyt = rz.objFromJSON((rz.strReplaceAll(lyt, "'", "\"")).trim()) }
          // eslint-disable-next-line max-len
          const ll = {i: item.key, w: lyt?.w ?? 8, h: lyt?.h ?? 8, x: lyt?.x ?? k, y: lyt?.y ?? k, minW: lyt?.minW, maxW: lyt?.maxW, minH: lyt?.minH, maxH: lyt?.maxH, isDraggable: !(lyt?.isDraggable === "false" || lyt?.isDraggable === false), isResizable: !(lyt?.isResizable === "false" || lyt?.isResizable === false), static: !(lyt?.static === "false" || lyt?.static === false)};

          if (cState.attributes.isDraggable || cState.attributes.draggable) { ll.isDraggable = ((cState.attributes.isDraggable || cState.attributes.draggable) === "true") }
          if (cState.attributes.isResizable || cState.attributes.resizable) { ll.isResizable = ((cState.attributes.isResizable || cState.attributes.resizable) === "true") }
          if (cState.attributes.static) { ll.static = (cState.attributes.static === "true") }
          if (cState.attributes.x) { ll.x = (Number(cState.attributes.x)) }
          if (cState.attributes.y) { ll.y = (Number(cState.attributes.y)) }
          if (cState.attributes.w) { ll.w = (Number(cState.attributes.w)) }
          if (cState.attributes.h) { ll.h = (Number(cState.attributes.h)) }
          if (cState.attributes.minW) { ll.minW = (Number(cState.attributes.minW)) }
          if (cState.attributes.maxW) { ll.maxW = (Number(cState.attributes.maxW)) }
          if (cState.attributes.minH) { ll.minH = (Number(cState.attributes.minH)) }
          if (cState.attributes.maxH) { ll.maxH = (Number(cState.attributes.maxH)) }
          // @ts-ignore
          louts.push(ll);

          if (ll) {
            chldrns.push(
              <div key={item.key} id={item.key} className={cellclass.join(" ")} data-grid={ll} onClick={(event) => { onClick({event, itemKey: item.key, allItems: content}) }}>
                {(item.content || []).map((el) => (el.children))}
              </div>
            );
          }
        }

        k++;
      }
    });

    if (attributes.layouts && rz.isString(attributes.layouts)) {
      if (attributes.layouts.startsWith("{") && attributes.layouts.endsWith("}")) {
        const jsn = rz.objFromJSON((rz.strReplaceAll(attributes.layouts, "'", "\"")).trim());
        // @ts-ignore
        if (jsn && jsn.lg) { setLayuot(jsn) }
      }
      else if (rootNamespace[attributes.layouts] && rz.isFunction(rootNamespace[attributes.layouts])) {
        const clientFNC = rootNamespace[attributes.layouts];
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, content, safe});
        if (ret && rz.isObject(ret) && ret.lg) { setLayuot(ret) }
      }
    }
    else if (isResponsive) {
      // @ts-ignore
      setLayuot({lg: louts.lg, md: louts.md, sm: louts.sm, xs: louts.xs, xxs: louts.xxs});
    }
    else {
      // @ts-ignore
      setLayuot(louts);
    }
    return chldrns;
  }, [appState, attributes, cmpType.responsive, cols, content, dct, key, lng, onClick, rootAttributes.key, rootNamespace, user, safe]);

  const onLayoutChange = useCallback((layout, lyouts) => {
    if (fnc.config?.isDev) console.log({action: "<ReactGridLayout key='" + key + "'> -> onLayoutChange()", layout, layouts: lyouts});
    const attr = attributes.onLayoutChange || attributes.onlayoutchange;
    if (rootNamespace && attr && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, layout, layouts: lyouts, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, layout, layouts: lyouts, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [attributes, dct, dispatch, key, lng, rootAttributes.key, rootNamespace, user, safe]);

  // ReDraw Desktop-container if User changes Screen-size:
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [dummyValue, setRedrawTimeStamp] = useState(() => ((new Date().getTime())));
  useLayoutEffect(() => { // On document was changed size
    function updateWindowSize1() { setRedrawTimeStamp((new Date().getTime())) }
    window.addEventListener("resize", updateWindowSize1);
    return () => window.removeEventListener("resize", updateWindowSize1);
  }, []);

  useLayoutEffect(() => {
    if (rz.isObject(cols) || cmpType.responsive) { return }

    const updateContainerWidth = () => {
      const width = nonResponsiveWrapperRef.current?.getBoundingClientRect?.().width || 0;
      setContainerWidth((prev) => (prev !== width ? width : prev));
    };

    updateContainerWidth();

    if (!window.ResizeObserver || !nonResponsiveWrapperRef.current) { return }

    const resizeObserver = new window.ResizeObserver(() => { updateContainerWidth() });
    resizeObserver.observe(nonResponsiveWrapperRef.current);

    return () => resizeObserver.disconnect();
  }, [cmpType.responsive, cols, dummyValue]);

  // useEffect(() => {
  //   if (fnc.config?.isDev) { console.log({breakpoints, cols, layouts, rowHeight, compactType: cmpType.compactType, allowOverlap: cmpType.allowOverlap, preventCollision: cmpType.preventCollision}) }
  // }, [cmpType, breakpoints, cols, rowHeight, layouts]);

  if (!layouts) {
    return (
      <div key={key || "react-grid-layout"} />
    );
  }

  if (rz.isObject(cols) || cmpType.responsive) {
    return (
      <ResponsiveGridLayout id={key || "react-grid-layout"} className={clsu.join(" ")} style={styl}
        compactType={cmpType.compactType} allowOverlap={cmpType.allowOverlap}
        preventCollision={cmpType.preventCollision}
        // @ts-ignore
        cols={cols} margin={margin} rowHeight={rowHeight} resizeHandles={resizeHandles} breakpoints={breakpoints}
        draggableHandle={attributes.draggableHandle ? (attributes.draggableHandle.startsWith(".") ? "" : ".") + attributes.draggableHandle : ".dragMe"}
        layouts={layouts} onLayoutChange={onLayoutChange}
      >
        {childrenItems.length && childrenItems}
      </ResponsiveGridLayout>
    );
  }

  if (!containerWidth) {
    return (
      <div ref={nonResponsiveWrapperRef} className={clsu.join(" ")} style={styl}>
        <div key={key || "react-grid-layout"} />
      </div>
    );
  }

  return (
    <div ref={nonResponsiveWrapperRef} className={clsu.join(" ")} style={styl}>
      <GridLayout id={key || "react-grid-layout"} className={clsu.join(" ")}
        compactType={cmpType.compactType} allowOverlap={cmpType.allowOverlap} preventCollision={cmpType.preventCollision}
        width={containerWidth} cols={cols} margin={margin} rowHeight={rowHeight} resizeHandles={resizeHandles}
        draggableHandle={attributes.draggableHandle ? (attributes.draggableHandle.startsWith(".") ? "" : ".") + attributes.draggableHandle : ".dragMe"}
        layout={layouts} onLayoutChange={onLayoutChange}
      >
        {childrenItems.length && childrenItems}
      </GridLayout>
    </div>
  );
}

export default RZreactGridLayout;
/* eslint-enable @typescript-eslint/no-unused-vars */
